/**
 * Переходы вертикального среза.
 *
 * Логика переходов вынесена из компонента: её можно проверить тестами без
 * отрисовки, а экран остаётся тонким (docs/ARCHITECTURE.md §3.2).
 */

import type { MasteryStat, SessionId, UnitOutcome } from '@type2x/shared-contracts';
import type { TypingSession } from '@type2x/typing-engine';
import { estimateFatigue, DEFAULT_LEARNING_CONFIG } from '@type2x/learning-engine';
import type { Workspace } from '@type2x/feature-personal-workspace';
import {
  achievementsEarnedAt,
  activeProfile,
  appendXp,
  recordSession,
  setBaseline,
  upsertMastery,
} from '@type2x/feature-personal-workspace';
import { initialiseWorkspace, type OnboardingChoice } from '@type2x/feature-onboarding';
import { analyzeDiagnostic, buildDiagnosticTask } from '@type2x/feature-diagnostics';
import type { DiagnosticResult } from '@type2x/feature-diagnostics';
import {
  applyOutcomes,
  buildPracticePlan,
  collectUnitOutcomes,
  type PracticePlan,
} from '@type2x/feature-adaptive-practice';
import { buildSessionSummary, type SessionOutcomeSummary } from '@type2x/feature-progress';
import { buildSessionPayload, type SessionPayload } from '@type2x/feature-sync';

import type { Runtime } from '../runtime.js';
import type { AppScreen, PractisedMode } from './app-state.js';

const CONFIG = DEFAULT_LEARNING_CONFIG;

/** Длительность первой персональной тренировки (ТЗ §16.3, шаг 7). */
export const FIRST_PRACTICE_MINUTES = 5;

/** Результат перехода: новое пространство и новый экран. */
export interface Transition {
  readonly workspace: Workspace;
  readonly screen: AppScreen;
  /**
   * Итог сессии для отправки на сервер.
   *
   * Заполняется только после тренировки. Отправка выполняется отдельно и не
   * входит в переход экрана: сеть не должна задерживать показ результата
   * (ТЗ FR-SY-04).
   */
  readonly sessionPayload?: SessionPayload;
}

/**
 * Собирает экран результата.
 *
 * Одна точка на все десять режимов. Взятые рубежи спрашиваются здесь, а не в
 * каждом режиме по отдельности: `recordSession` отмечает их той же меткой
 * времени, и любой, кто соберёт экран сам, однажды забудет спросить — так уже
 * вышло с отметкой дня практики (см. `recordSession`).
 *
 * @param workspace пространство уже после `recordSession`;
 * @param now та же метка времени, с которой оно записывалось.
 */
export function resultScreen(
  workspace: Workspace,
  summary: SessionOutcomeSummary,
  practised: PractisedMode,
  now: string,
): Extract<AppScreen, { name: 'result' }> {
  return { name: 'result', summary, practised, earned: achievementsEarnedAt(workspace, now) };
}

/** Создаёт гостевое пространство и открывает диагностику. */
export function startFromOnboarding(runtime: Runtime, choice: OnboardingChoice): Transition {
  const now = runtime.clock.isoNow();
  const workspace = initialiseWorkspace(
    { kind: 'guest', installationId: runtime.installationId },
    choice,
    choice.language,
    now,
  );

  return {
    workspace,
    screen: {
      name: 'diagnostics',
      task: buildDiagnosticTask(choice.language, choice.layoutId, now),
      sessionId: runtime.ids.newSessionId(),
    },
  };
}

/**
 * Открывает диагностику для уже существующего пространства.
 *
 * Прежняя скорость передаётся заданию: повторное измерение подстраивает
 * длину под того, кто печатает быстро, — двадцати четырёх слов ему мало на
 * устойчивое число.
 */
export function resumeDiagnostics(runtime: Runtime, workspace: Workspace): AppScreen {
  const profile = activeProfile(workspace);
  return {
    name: 'diagnostics',
    task: buildDiagnosticTask(
      profile.language,
      profile.keyboard.layoutId,
      runtime.clock.isoNow(),
      profile.baseline?.cpm,
    ),
    sessionId: runtime.ids.newSessionId(),
  };
}

/**
 * Завершает диагностику: фиксирует baseline и слабые места и показывает итог.
 *
 * Тренировка строится не здесь, а когда человек нажмёт «продолжить»: между
 * измерением и занятием он читает, что про него выяснили, и может решить
 * иначе — начать с основ или пройти измерение заново.
 */
export function finishDiagnostics(
  runtime: Runtime,
  workspace: Workspace,
  words: readonly string[],
  session: TypingSession,
): Transition {
  const now = runtime.clock.isoNow();
  const profile = activeProfile(workspace);

  const diagnostic: DiagnosticResult = analyzeDiagnostic(
    profile.language,
    profile.keyboard.layoutId,
    words,
    session.keystrokeEvents,
    session.typingErrors,
    session.metrics,
    CONFIG,
    now,
  );

  let next = setBaseline(workspace, diagnostic.baseline, now);
  next = upsertMastery(next, diagnostic.units, diagnostic.stats, now);
  next = recordSession(next, session.metrics, now);

  return {
    workspace: next,
    screen: { name: 'diagnostic-result', diagnostic },
  };
}

/** Экран тренировки со свежим планом и новым идентификатором сессии. */
export function practiceScreen(runtime: Runtime, workspace: Workspace): AppScreen {
  return {
    name: 'practice',
    plan: planFor(runtime, workspace),
    sessionId: runtime.ids.newSessionId(),
  };
}

/** Строит план тренировки по текущему состоянию навыка. */
export function planFor(runtime: Runtime, workspace: Workspace): PracticePlan {
  const profile = activeProfile(workspace);

  return buildPracticePlan(
    {
      snapshot: {
        language: profile.language,
        layoutId: profile.keyboard.layoutId,
        units: profile.units,
        stats: profile.stats,
        seenInControl: profile.seenInControl,
        leftHandShare: 0.5,
      },
      durationMinutes: FIRST_PRACTICE_MINUTES,
      seed: `${runtime.installationId}:${runtime.clock.epochMs()}`,
      nowMs: runtime.clock.epochMs(),
      hintLevel: 'zone-highlight',
      accuracy: profile.baseline?.accuracy ?? 1,
      fatigue: 0,
      effortlessSessions: 0,
    },
    CONFIG,
  );
}

/** Завершает тренировку: обновляет mastery, начисляет XP и собирает итог. */
export function finishPractice(
  runtime: Runtime,
  workspace: Workspace,
  plan: PracticePlan,
  session: TypingSession,
  sessionId: SessionId,
): Transition {
  const now = runtime.clock.isoNow();
  const profile = activeProfile(workspace);

  const outcomes = collectUnitOutcomes(
    plan.exercise,
    session.keystrokeEvents,
    session.typingErrors,
  );
  const statsAfter = applyOutcomes(profile.units, profile.stats, outcomes, CONFIG, now);

  let next = upsertMastery(workspace, [], statsAfter, now);
  next = recordSession(next, session.metrics, now);

  const summary: SessionOutcomeSummary = buildSessionSummary({
    metrics: session.metrics,
    baseline: profile.baseline,
    units: profile.units,
    statsBefore: profile.stats,
    statsAfter,
    previousXp: next.xpLedger.reduce((sum, item) => sum + item.amount, 0),
    xpOccurrencesToday: countXpToday(next, now),
    activeDays: activeProfile(next).practiceDays.length,
    retained: false,
    isControlled: false,
    awardedAt: now,
    config: CONFIG,
  });

  for (const transaction of summary.xpAwarded) {
    next = appendXp(next, transaction, now);
  }

  return {
    workspace: next,
    screen: resultScreen(next, summary, { kind: 'practice' }, now),
    sessionPayload: payloadFor(runtime, workspace, plan, session, sessionId, statsAfter, outcomes),
  };
}

/** Собирает итог сессии для отправки на сервер. */
function payloadFor(
  runtime: Runtime,
  workspace: Workspace,
  plan: PracticePlan,
  session: TypingSession,
  sessionId: SessionId,
  statsAfter: readonly MasteryStat[],
  outcomes: readonly UnitOutcome[],
): SessionPayload {
  const profile = activeProfile(workspace);

  // Начало сессии восстанавливается по её длительности: монотонные часы
  // измеряют интервал, а календарная метка нужна серверу для отчётов.
  const startedAt = new Date(runtime.clock.epochMs() - session.metrics.elapsedMs).toISOString();

  return buildSessionPayload({
    syncId: runtime.ids.newSyncId(),
    sessionId,
    mode: plan.exercise.mode,
    language: profile.language,
    layoutId: profile.keyboard.layoutId,
    startedAt,
    durationMs: session.metrics.elapsedMs,
    metrics: session.metrics,
    outcomes,
    statsAfter,
    errors: session.typingErrors,
    isControlled: false,
  });
}

/** Сколько раз каждая причина XP уже сработала сегодня. */
export function countXpToday(
  workspace: Workspace,
  nowIso: string,
): Readonly<Partial<Record<string, number>>> {
  const today = nowIso.slice(0, 10);
  const counts: Record<string, number> = {};

  for (const transaction of workspace.xpLedger) {
    if (transaction.awardedAt.slice(0, 10) !== today) continue;
    counts[transaction.reason] = (counts[transaction.reason] ?? 0) + 1;
  }
  return counts;
}

/** Оценка усталости по половинам сессии. */
export function fatigueOf(session: TypingSession): number {
  const intervals = session.interKeyIntervals;
  if (intervals.length < 4) return 0;

  const middle = Math.floor(intervals.length / 2);
  const mean = (values: readonly number[]): number =>
    values.length === 0 ? 0 : values.reduce((sum, value) => sum + value, 0) / values.length;

  return estimateFatigue(
    mean(intervals.slice(0, middle)),
    mean(intervals.slice(middle)),
    session.metrics.accuracy,
    session.metrics.accuracy,
  );
}
